import React, { useState } from 'react';
import {
  Td,
  Text,
  Badge,
  IconButton,
  HStack,
  Input,
  Select,
  Flex,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import { EditIcon, CheckIcon, CloseIcon, CalendarIcon } from '@chakra-ui/icons';
import api from '../../services/api';
import { formatRelativeTime } from '../leads/NewKB/utils/NewKBDataUtils';
import { getStatusColor, getPriorityColor, formatUserEmail } from './TasksUtils';

/**
 * Renders the status cell with toggle functionality
 */
export const StatusCell = ({ task, onStatusChange }) => {
  return (
    <Td py="1" px="0" width="90px" textAlign="left">
      <Badge colorScheme={getStatusColor(task.status)} px={2} py={1}>
        {task.status}
      </Badge>
    </Td>
  );
};

/**
 * Renders the ID cell
 */
export const IdCell = ({ task }) => (
  <Td py="1" px="0" fontSize="xs">
    {task.leadId || 'No ID'}
  </Td>
);

/**
 * Renders the bride and groom cell
 */
export const BrideGroomCell = ({ task }) => (
  <Td py="1" px="0">
    <Text fontSize="sm" fontWeight="medium">
      {task.leadDetails?.brideAndGroomName || 'Unknown'}
    </Text>
  </Td>
);

/**
 * Renders the assigned user cell
 */
export const AssignedCell = ({ task, lead }) => (
  <Td py="1" px="0">
    <Text fontSize="sm">
      {lead?.assignedTo ? formatUserEmail(lead.assignedTo) : 'Unassigned'}
    </Text>
  </Td>
);

/**
 * Renders the follow up cell
 */
export const FollowUpCell = ({ task }) => (
  <Td py="1" px="0">
    <Text fontSize="xs">
      {task.leadDetails?.nextFollowUpTime ? formatRelativeTime(task.leadDetails.nextFollowUpTime) : 'No follow-up'}
    </Text>
  </Td>
);


/**
 * Renders the title cell with edit capability
 */
export const TitleCell = ({ task, onTitleChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);

  const handleEditSave = () => {
    onTitleChange(task._id, editTitle);
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditTitle(task.title);
    setIsEditing(false);
  };

  return (
    <Td py="1" px="0">
      {isEditing ? (
        <HStack spacing={0.5}>
          <Input 
            value={editTitle} 
            onChange={(e) => setEditTitle(e.target.value)} 
            size="xs" 
            autoFocus 
          />
          <IconButton 
            icon={<CheckIcon />} 
            size="xs" 
            aria-label="Save changes" 
            onClick={handleEditSave} 
          />
          <IconButton 
            icon={<CloseIcon />} 
            size="xs" 
            aria-label="Cancel changes" 
            onClick={handleEditCancel}
          />
        </HStack>
      ) : (
        <Flex justify="space-between" align="center">
          <Text 
            fontSize="sm" 
            isTruncated={false} 
            width="400px" 
            overflowWrap="break-word" 
            whiteSpace="normal"
            lineHeight="shorter"
          >
            {task.title}
          </Text>
          <IconButton 
            icon={<EditIcon />} 
            size="xs" 
            aria-label="Edit task" 
            onClick={() => setIsEditing(true)} 
            variant="ghost"
            ml={1}
            flexShrink={0}
          />
        </Flex>
      )}
    </Td>
  );
};

/**
 * Renders the priority cell
 */
export const PriorityCell = ({ task }) => (
  <Td py="1" px="0" width="80px" textAlign="left">
    <Badge colorScheme={getPriorityColor(task.priority)} px={2} py={1}>
      {task.priority}
    </Badge>
  </Td>
);

/**
 * Renders the task assignee cell with edit capability
 */
export const TaskAssigneeCell = ({ task, users, onStatusChange }) => {
  const [isEditingAssignee, setIsEditingAssignee] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(task.assignedTo?.uid || '');

  const handleAssigneeChange = async (newAssigneeUid) => {
    try {
      const selectedUser = users.find(user => user.uid === newAssigneeUid);
      const assignedTo = newAssigneeUid ? {
        uid: newAssigneeUid,
        email: selectedUser?.email || ''
      } : { uid: '', email: '' };
      
      // Update the task with the new assignee
      await api.put(`/api/tasks/${task._id}`, { assignedTo });
      
      // Update local state to reflect the change
      setSelectedAssignee(newAssigneeUid);
      setIsEditingAssignee(false);
      
      // Notify parent component about the change
      if (typeof onStatusChange === 'function') {
        onStatusChange(task._id, task.status, true); // Pass true to indicate it's an assignee change
      }
    } catch (error) {
      console.error('[TaskAssigneeCell] Error updating task assignee:', error);
    }
  };

  return (
    <Td py="1" px="0">
      {isEditingAssignee ? (
        <HStack spacing={0.5}>
          <Select 
            size="sm" 
            value={selectedAssignee}
            onChange={(e) => handleAssigneeChange(e.target.value)}
            maxW="120px"
            autoFocus
          >
            <option value="">Unassigned</option>
            {users.map(user => (
              <option key={user.uid} value={user.uid}>
                {user.displayName || user.email}
              </option>
            ))}
          </Select>
          <IconButton 
            icon={<CloseIcon />} 
            size="xs" 
            aria-label="Cancel editing" 
            onClick={() => setIsEditingAssignee(false)}
          />
        </HStack>
      ) : (
        <Flex justify="space-between" align="center">
          <Text fontSize="sm">
            {task.assignedTo?.email ? formatUserEmail(task.assignedTo.email) : 'Unassigned'}
          </Text>
          <IconButton 
            icon={<EditIcon />} 
            size="xs" 
            aria-label="Edit assignee" 
            onClick={() => setIsEditingAssignee(true)}
            variant="ghost"
            ml={1}
            flexShrink={0}
          />
        </Flex>
      )}
    </Td>
  );
};

/**
 * Renders the due date cell with edit capability
 */
export const DueDateCell = ({ task, onStatusChange }) => {
  const [isEditingDueDate, setIsEditingDueDate] = useState(false);
  const [dueDate, setDueDate] = useState(task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '');
  const toast = useToast();

  const handleDueDateUpdate = async () => {
    try {
      // Update the task with the new due date
      await api.put(`/api/tasks/${task._id}`, { 
        dueDate: dueDate ? new Date(dueDate).toISOString() : null 
      });
      
      // Update local state to reflect the change
      setIsEditingDueDate(false);
      
      // Notify parent component about the change
      if (typeof onStatusChange === 'function') {
        onStatusChange(task._id, task.status, false);
      }
      
      toast({
        title: "Due date updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('[DueDateCell] Error updating due date:', error);
      toast({
        title: "Failed to update due date",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Td py="1" px="0">
      {isEditingDueDate ? (
        <HStack spacing={0.5}>
          <Input
            type="date"
            size="xs"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            autoFocus
            fontSize="xs"
          />
          <IconButton 
            icon={<CheckIcon />} 
            size="xs" 
            aria-label="Save due date" 
            onClick={handleDueDateUpdate}
          />
          <IconButton 
            icon={<CloseIcon />} 
            size="xs" 
            aria-label="Cancel date changes" 
            onClick={() => {
              setIsEditingDueDate(false);
              setDueDate(task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '');
            }}
          />
        </HStack>
      ) : (
        <Flex justify="space-between" align="center">
          <Flex align="center">
            <Text fontSize="xs" fontWeight="medium" mr={1}>
              Task Due:
            </Text>
            <Text 
              fontSize="xs" 
              color={task.dueDate ? 
                (new Date(task.dueDate) < new Date() ? 'red.500' : 
                 new Date(task.dueDate) < new Date(Date.now() + 86400000 * 2) ? 'orange.500' : 
                 'gray.600') : 
                'gray.600'}
            >
              {task.dueDate ? formatRelativeTime(task.dueDate) : 'No due date'}
            </Text>
          </Flex>
          <IconButton 
            icon={<CalendarIcon />} 
            size="xs" 
            aria-label="Edit due date" 
            onClick={() => setIsEditingDueDate(true)}
            variant="ghost"
            ml={1}
            flexShrink={0}
          />
        </Flex>
      )}
    </Td>
  );
};
